import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Upload, X, File, Image, FileText, FileSpreadsheet, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../services/api';

// File type icons based on MIME type or extension
const getFileIcon = (mimeType, filename) => {
  if (mimeType?.startsWith('image/')) return Image;
  if (mimeType?.includes('spreadsheet') || filename?.match(/\.(xlsx?|csv)$/i)) return FileSpreadsheet;
  if (mimeType?.includes('pdf') || mimeType?.includes('document') || mimeType?.includes('text')) return FileText;
  return File;
};

// Format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

// Fade duration in ms
const FADE_OUT_DELAY = 3000; // Wait 3 seconds before starting fade
const FADE_DURATION = 2000; // 2 second fade animation

// File status component
const FileStatus = ({ status, progress, isFading }) => {
  if (isFading) {
    return (
      <div className="flex items-center gap-2">
        <CheckCircle2 className="w-4 h-4 text-green-500" />
        <span className="text-xs text-green-500">Done!</span>
      </div>
    );
  }
  
  switch (status) {
    case 'uploading':
      return (
        <div className="flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
          <span className="text-xs text-blue-500">{progress}%</span>
        </div>
      );
    case 'complete':
      return <CheckCircle2 className="w-4 h-4 text-green-500" />;
    case 'error':
      return <AlertCircle className="w-4 h-4 text-red-500" />;
    default:
      return null;
  }
};

const FileUploader = ({
  organizationId,
  productId,
  entityType,
  entityId = null,
  tempBatchId = null,
  onUploadingChange,
  onFilesChange,
  onUploadComplete, // New: callback when files complete and fade out
  maxFiles = 10,
  maxSizeMB = 500,
  accept,
  disabled = false,
  className
}) => {
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [fadingFiles, setFadingFiles] = useState(new Set()); // Track files that are fading
  const fileInputRef = useRef(null);
  const abortControllersRef = useRef({});
  const fadeTimersRef = useRef({});

  // Handle fade out and removal of completed files
  useEffect(() => {
    files.forEach(file => {
      if (file.status === 'complete' && !fadingFiles.has(file.localId) && !fadeTimersRef.current[file.localId]) {
        // Start fade timer for this file
        fadeTimersRef.current[file.localId] = setTimeout(() => {
          // Mark as fading
          setFadingFiles(prev => new Set([...prev, file.localId]));
          
          // After fade animation, remove the file
          setTimeout(() => {
            setFiles(prev => prev.filter(f => f.localId !== file.localId));
            setFadingFiles(prev => {
              const next = new Set(prev);
              next.delete(file.localId);
              return next;
            });
            delete fadeTimersRef.current[file.localId];
            
            // Trigger refresh callback
            onUploadComplete?.();
          }, FADE_DURATION);
        }, FADE_OUT_DELAY);
      }
    });
    
    // Cleanup timers on unmount
    const timersRef = fadeTimersRef.current;
    return () => {
      Object.values(timersRef).forEach(timer => clearTimeout(timer));
    };
  }, [files, fadingFiles, onUploadComplete]);

  // PUT file directly to S3 via presigned URL with progress tracking
  const putToS3 = (url, body, contentType, abortController, onProgress) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(e.loaded, e.total);
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const etag = xhr.getResponseHeader('ETag');
          resolve(etag);
        } else {
          reject(new Error(`S3 upload failed (${xhr.status})`));
        }
      };

      xhr.onerror = () => reject(new Error('Network error during upload'));
      xhr.onabort = () => reject(new Error('Upload cancelled'));
      abortController.signal.addEventListener('abort', () => xhr.abort());

      xhr.open('PUT', url);
      xhr.setRequestHeader('Content-Type', contentType);
      xhr.send(body);
    });
  };

  // Upload a single file directly to S3 via presigned URLs
  const uploadFile = useCallback(async (file, fileId) => {
    const abortController = new AbortController();
    abortControllersRef.current[fileId] = abortController;
    const contentType = file.type || 'application/octet-stream';

    try {
      // Step 1: Initiate — get presigned URL(s) from backend
      const initResponse = await api.post('/api/files/initiate', {
        organization_id: organizationId,
        product_id: productId,
        entity_type: entityType,
        entity_id: entityId,
        temp_batch_id: tempBatchId,
        filename: file.name,
        content_type: contentType,
        size_bytes: file.size
      });

      const initData = initResponse.data;
      const { file_id, use_multipart } = initData;

      setFiles(prev => prev.map(f =>
        f.localId === fileId ? { ...f, file_id } : f
      ));

      // Step 2: Upload directly to S3
      let completeParts = null;

      if (!use_multipart) {
        // Simple PUT
        await putToS3(initData.upload_url, file, contentType, abortController, (loaded, total) => {
          const progress = Math.round((loaded / total) * 100);
          setFiles(prev => prev.map(f =>
            f.localId === fileId ? { ...f, progress } : f
          ));
        });
      } else {
        // Multipart: upload each chunk, collect ETags
        const { part_urls, part_size, upload_id } = initData;
        const parts = [];
        let totalUploaded = 0;

        for (const { part_number, url } of part_urls) {
          const start = (part_number - 1) * part_size;
          const end = Math.min(start + part_size, file.size);
          const chunk = file.slice(start, end);

          const etag = await putToS3(url, chunk, contentType, abortController, (loaded) => {
            const progress = Math.round(((totalUploaded + loaded) / file.size) * 100);
            setFiles(prev => prev.map(f =>
              f.localId === fileId ? { ...f, progress } : f
            ));
          });

          totalUploaded += (end - start);
          parts.push({ PartNumber: part_number, ETag: etag });
        }

        completeParts = { upload_id, parts };
      }

      // Step 3: Tell backend the upload is done
      await api.post('/api/files/complete', {
        file_id,
        upload_id: completeParts?.upload_id || null,
        parts: completeParts?.parts || null
      });

      setFiles(prev => prev.map(f =>
        f.localId === fileId ? { ...f, status: 'complete', progress: 100 } : f
      ));
      toast.success(`Uploaded ${file.name}`);

    } catch (error) {
      if (error.name === 'AbortError' || error.message === 'Upload cancelled') return;

      console.error('Upload failed:', error);
      setFiles(prev => prev.map(f =>
        f.localId === fileId ? { ...f, status: 'error', error: error.message || 'Upload failed' } : f
      ));
      toast.error(`Failed to upload ${file.name}`);
    } finally {
      delete abortControllersRef.current[fileId];
    }
  }, [organizationId, productId, entityType, entityId, tempBatchId]);

  // Handle file selection
  const handleFiles = useCallback((selectedFiles) => {
    const fileArray = Array.from(selectedFiles);
    
    // Validate
    if (files.length + fileArray.length > maxFiles) {
      toast.error(`Maximum ${maxFiles} files allowed`);
      return;
    }

    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    const validFiles = fileArray.filter(file => {
      if (file.size > maxSizeBytes) {
        toast.error(`${file.name} exceeds ${maxSizeMB}MB limit`);
        return false;
      }
      return true;
    });

    // Add files to state and start upload
    const newFiles = validFiles.map(file => ({
      localId: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      status: 'uploading',
      progress: 0,
      error: null
    }));

    setFiles(prev => [...prev, ...newFiles]);
    
    // Start uploads
    newFiles.forEach(f => uploadFile(f.file, f.localId));
  }, [files.length, maxFiles, maxSizeMB, uploadFile]);

  // Drag handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragging(true);
  }, [disabled]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (disabled) return;
    
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      handleFiles(droppedFiles);
    }
  }, [disabled, handleFiles]);

  // Remove file
  const removeFile = useCallback((fileId) => {
    // Cancel upload if in progress
    if (abortControllersRef.current[fileId]) {
      abortControllersRef.current[fileId].abort();
    }

    const fileToRemove = files.find(f => f.localId === fileId);
    
    // If file was uploaded, delete from server
    if (fileToRemove?.file_id && fileToRemove.status === 'complete') {
      api.delete(`/api/files/${fileToRemove.file_id}`).catch(console.error);
    }

    setFiles(prev => {
      const newFiles = prev.filter(f => f.localId !== fileId);
      onFilesChange?.(newFiles.filter(f => f.status === 'complete'));
      onUploadingChange?.(newFiles.some(f => f.status === 'uploading'));
      return newFiles;
    });
  }, [files, onFilesChange, onUploadingChange]);

  return (
    <div className={cn("space-y-3", className)}>
      {/* Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !disabled && fileInputRef.current?.click()}
        className={cn(
          "relative border-2 border-dashed rounded-lg p-6 transition-all cursor-pointer",
          "flex flex-col items-center justify-center gap-2",
          isDragging && "border-blue-500 bg-blue-50",
          disabled && "opacity-50 cursor-not-allowed",
          !isDragging && !disabled && "border-gray-300 hover:border-gray-400 hover:bg-gray-50"
        )}
      >
        <Upload className={cn("w-8 h-8", isDragging ? "text-blue-500" : "text-gray-400")} />
        <div className="text-center">
          <p className="text-sm font-medium text-gray-700">
            {isDragging ? "Drop files here" : "Drag & drop files here"}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            or click to browse • Max {maxSizeMB}MB per file
          </p>
        </div>
        
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={(e) => handleFiles(e.target.files)}
          className="hidden"
          accept={accept?.join(',')}
          disabled={disabled}
        />
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((file) => {
            const FileIcon = getFileIcon(file.type, file.name);
            
            return (
              <div
                key={file.localId}
                className={cn(
                  "flex items-center gap-3 p-3 rounded-lg border transition-all duration-500",
                  file.status === 'error' && "border-red-200 bg-red-50",
                  file.status === 'complete' && "border-green-200 bg-green-50",
                  file.status === 'uploading' && "border-blue-200 bg-blue-50",
                  fadingFiles.has(file.localId) && "opacity-0 transform -translate-x-4"
                )}
              >
                <FileIcon className={cn(
                  "w-8 h-8 flex-shrink-0",
                  file.status === 'error' && "text-red-400",
                  file.status === 'complete' && "text-green-500",
                  file.status === 'uploading' && "text-blue-400"
                )} />
                
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" title={file.name}>
                    {file.name}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">
                      {formatFileSize(file.size)}
                    </span>
                    {file.error && (
                      <span className="text-xs text-red-500">{file.error}</span>
                    )}
                  </div>
                  
                  {file.status === 'uploading' && (
                    <Progress value={file.progress} className="h-1 mt-1" />
                  )}
                </div>

                <FileStatus status={file.status} progress={file.progress} isFading={fadingFiles.has(file.localId)} />
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile(file.localId);
                  }}
                  className="flex-shrink-0 h-8 w-8 p-0 text-gray-400 hover:text-red-500"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FileUploader;
